<template>
  <div class="teacher-layout">
    <div class="top-bar">
      <div class="top-bar-content">
        <div class="page-title">{{ pageTitle }}</div>
      </div>
    </div>
    <div v-if="!isCollapse && isMobile" class="sidebar-mask" @click="toggleSidebar"></div>
    <el-container>
      <el-aside 
        class="sidebar-container" 
        :style="sidebarStyle">
        <div class="sidebar-header">
          <div class="logo-wrapper">
            <div class="logo">托管</div>
          </div>
          <div class="title-wrapper">
            <div class="school-title">托管中心</div>
            <div class="module-title">教师中心</div>
          </div>
        </div>
        
        <div class="sidebar-nav">
          <div class="nav-group">
            <div class="nav-group-title">教师导航</div>
            <el-menu
              :default-active="activeMenu"
              class="sidebar-menu"
              router
              :collapse="isCollapse"
              background-color="#fff"
              text-color="#303133"
              active-text-color="#4f46e5">
              <el-menu-item :index="getRoutePath('')">
                <i class="el-icon-s-home"></i>
                <span slot="title">首页</span>
              </el-menu-item>
              <el-menu-item :index="getRoutePath('attendance')">
                <i class="el-icon-time"></i>
                <span slot="title">签到管理</span>
              </el-menu-item>
              <el-menu-item :index="getRoutePath('rating')">
                <i class="el-icon-star-on"></i>
                <span slot="title">学生评分</span>
              </el-menu-item>
              <el-menu-item :index="getRoutePath('assignments')">
                <i class="el-icon-notebook-1"></i>
                <span slot="title">学生作业</span>
              </el-menu-item>
              <el-menu-item :index="getRoutePath('scores')">
                <i class="el-icon-s-data"></i>
                <span slot="title">学生成绩</span>
              </el-menu-item>
              <el-menu-item :index="getRoutePath('communication')">
                <i class="el-icon-chat-line-square"></i>
                <span slot="title" class="communication-menu-item">
                  家长沟通
                  <el-badge v-if="unreadCount > 0" :value="unreadCount" class="communication-badge" type="danger" />
                </span>
              </el-menu-item>
              <el-menu-item :index="getRoutePath('wrong-questions')">
                <i class="el-icon-warning"></i>
                <span slot="title">错题记录</span>
              </el-menu-item>
              <el-menu-item :index="getRoutePath('documents')">
                <i class="el-icon-folder-opened"></i>
                <span slot="title">文库查看</span>
              </el-menu-item>
            </el-menu>
          </div>
        </div>
      </el-aside>
      
      <el-container class="main-container" :style="mainContainerStyle">
        <el-header height="60px" class="header">
          <div class="header-left">
            <div class="collapse-btn-wrapper">
              <i 
                :class="isCollapse ? 'el-icon-s-unfold' : 'el-icon-s-fold'" 
                class="collapse-btn"
                @click="toggleSidebar"></i>
              <span v-if="unreadCount > 0" class="message-badge"></span>
            </div>
          </div>
          <div class="header-right">
            <el-dropdown trigger="click">
              <div class="avatar-container">
                <el-avatar :size="32" icon="el-icon-user-solid"></el-avatar>
                <span class="name">{{ userName }}</span>
                <i class="el-icon-caret-bottom"></i>
              </div>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item @click.native="openProfileDialog">个人信息</el-dropdown-item>
                <el-dropdown-item @click.native="openPhoneDialog">更新手机号</el-dropdown-item>
                <el-dropdown-item @click.native="openPasswordDialog">修改密码</el-dropdown-item>
                <el-dropdown-item divided @click.native="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </el-header>
        
        <el-main class="main-content">
          <router-view />
        </el-main>
        
        <!-- 个人信息对话框 -->
        <el-dialog
          title="个人信息"
          :visible.sync="profileDialogVisible"
          width="90%"
          top="5vh"
          :fullscreen="false"
          custom-class="profile-dialog"
          :class="{'mobile-dialog': isMobile}">
          <div v-loading="profileLoading" class="profile-container">
            <div v-if="profileData" class="user-profile">
              <div class="profile-header">
                <el-avatar :size="80" :src="profileData.avatar" icon="el-icon-user-solid"></el-avatar>
                <div class="user-name">{{ profileData.teacher_info?.real_name || profileData.username }}</div>
                <div class="user-role">教师</div>
              </div>
              
              <el-divider content-position="left">基本信息</el-divider>
              
              <div class="info-section">
                <div class="info-item">
                  <div class="info-label">用户名</div>
                  <div class="info-value">{{ profileData.username || '无' }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">手机号</div>
                  <div class="info-value">{{ profileData.phone || '无' }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">性别</div>
                  <div class="info-value">{{ getGenderText(profileData.gender) }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">状态</div>
                  <div class="info-value">
                    <el-tag :type="profileData.status === 1 ? 'success' : 'info'" size="small">
                      {{ profileData.status === 1 ? '正常' : '禁用' }}
                    </el-tag>
                  </div>
                </div>
              </div>
              
              <el-divider content-position="left">教师信息</el-divider>
              
              <div class="info-section" v-if="profileData.teacher_info">
                <div class="info-item">
                  <div class="info-label">姓名</div>
                  <div class="info-value">{{ profileData.teacher_info.real_name || '无' }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">教师编号</div>
                  <div class="info-value">{{ profileData.teacher_info.teacher_code || '无' }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">学科</div>
                  <div class="info-value">{{ profileData.teacher_info.course || '无' }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">职位</div>
                  <div class="info-value">{{ profileData.teacher_info.position || '无' }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">雇佣类型</div>
                  <div class="info-value">
                    {{ profileData.teacher_info.employment_type === 1 ? '全职' : profileData.teacher_info.employment_type === 2 ? '兼职' : '未知' }}
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-label">入职日期</div>
                  <div class="info-value">{{ profileData.teacher_info.entry_date || '无' }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">学历</div>
                  <div class="info-value">{{ profileData.teacher_info.education || '无' }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">专业</div>
                  <div class="info-value">{{ profileData.teacher_info.major || '无' }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">联系电话</div>
                  <div class="info-value">{{ profileData.teacher_info.contact_phone || '无' }}</div>
                </div>
              </div>
              
              <el-divider content-position="left">校区信息</el-divider>
              
              <div class="campus-section">
                <div v-for="(campus, index) in profileData.campus_info" :key="index" class="campus-item">
                  <div class="campus-name">
                    {{ campus.campus_name || '未知校区' }}
                    <el-tag v-if="campus.is_main" size="mini" type="success">主校区</el-tag>
                  </div>
                  <div class="campus-details">
                    <div class="campus-detail-item">
                      <i class="el-icon-location"></i>
                      <span>{{ campus.address || '无地址信息' }}</span>
                    </div>
                    <div class="campus-detail-item">
                      <i class="el-icon-phone"></i>
                      <span>{{ campus.contact_phone || '无联系电话' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-else-if="!profileLoading" class="empty-profile">
              <i class="el-icon-warning-outline"></i>
              <div class="empty-text">暂无个人信息数据</div>
            </div>
          </div>
          <span slot="footer" class="dialog-footer">
            <el-button @click="profileDialogVisible = false">关闭</el-button>
          </span>
        </el-dialog>
        
        <!-- 修改密码对话框 -->
        <el-dialog
          title="修改密码"
          :visible.sync="passwordDialogVisible"
          width="90%"
          top="15vh"
          :fullscreen="false"
          custom-class="password-dialog"
          :class="{'mobile-dialog': isMobile}">
          <el-form 
            ref="passwordForm" 
            :model="passwordForm" 
            :rules="passwordRules" 
            label-width="100px"
            status-icon>
            <el-form-item label="旧密码" prop="old_password">
              <el-input 
                v-model="passwordForm.old_password" 
                type="password" 
                placeholder="请输入旧密码"
                show-password></el-input>
            </el-form-item>
            <el-form-item label="新密码" prop="new_password">
              <el-input 
                v-model="passwordForm.new_password" 
                type="password" 
                placeholder="请输入新密码"
                show-password></el-input>
            </el-form-item>
            <el-form-item label="确认密码" prop="confirm_password">
              <el-input 
                v-model="passwordForm.confirm_password" 
                type="password" 
                placeholder="请再次输入新密码"
                show-password></el-input>
            </el-form-item>
            <el-form-item>
              <div class="password-action-buttons">
                <el-button type="primary" size="small" :loading="changingPassword" @click="submitPasswordForm">确认修改</el-button>
                <el-button size="small" @click="resetPasswordForm">重置</el-button>
              </div>
            </el-form-item>
          </el-form>
        </el-dialog>
        
        <!-- 更新手机号对话框 -->
        <phone-dialog v-model="phoneDialogVisible" :user-profile="profileData" @phone-updated="handlePhoneUpdated"></phone-dialog>
        
        <el-footer height="40px" class="footer">
          <div class="footer-content">
            <span>© {{ currentYear }} 校园管理系统</span>
          </div>
        </el-footer>
      </el-container>
    </el-container>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { getUserProfile, changePassword } from '@/api/user';
import PhoneDialog from '@/components/PhoneDialog.vue';
import { getUnreadCount } from '@/api/message';

export default {
  name: 'TeacherLayout',
  components: {
    PhoneDialog
  },
  data() {
    // 确认密码的验证函数
    const validateConfirmPassword = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('请再次输入密码'));
      } else if (value !== this.passwordForm.new_password) {
        callback(new Error('两次输入密码不一致'));
      } else {
        callback();
      }
    };
    
    return {
      isMobile: false,
      // 个人信息对话框
      profileDialogVisible: false,
      profileLoading: false,
      profileData: null,
      
      // 修改密码对话框
      passwordDialogVisible: false,
      changingPassword: false,
      passwordForm: {
        old_password: '',
        new_password: '',
        confirm_password: ''
      },
      passwordRules: {
        old_password: [
          { required: true, message: '请输入旧密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能小于6个字符', trigger: 'blur' }
        ],
        new_password: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能小于6个字符', trigger: 'blur' }
        ],
        confirm_password: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ]
      },
      // 更新手机号对话框
      phoneDialogVisible: false,
      // 未读消息数量
      unreadCount: 0,
      // 未读消息定时器
      unreadCountTimer: null
    };
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
    
    // 根据设备类型设置侧边栏初始状态
    if (this.isMobile) {
      // 移动端折叠侧边栏
      if (!this.isCollapse) {
        this.$store.dispatch('toggleSidebar', true);
      }
    } else {
      // 桌面端展开侧边栏
      if (this.isCollapse) {
        this.$store.dispatch('toggleSidebar', false);
      }
    }
    
    // 确保路由导航正确工作
    console.log('当前路径:', this.$route.path);
    console.log('学校代码:', this.$route.params.schoolCode || localStorage.getItem('schoolCode') || 'default');
    
    // 强制刷新未读消息状态
    this.refreshUnreadStatus();
    
    // 设置定时器，每隔3分钟获取一次未读消息数量
    this.unreadCountTimer = setInterval(() => {
      this.fetchUnreadCount();
    }, 3 * 60 * 1000); // 3分钟
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
    
    // 清除未读消息定时器
    if (this.unreadCountTimer) {
      clearInterval(this.unreadCountTimer);
    }
  },
  watch: {
    // 监听路由变化，在移动端下自动隐藏侧边栏
    '$route'() {
      if (this.isMobile && !this.isCollapse) {
        this.$store.dispatch('toggleSidebar', true);
      }
      // 滚动到页面顶部
      window.scrollTo(0, 0);
      // 确保内容区域也滚动到顶部
      this.$nextTick(() => {
        const mainContent = document.querySelector('.main-content');
        if (mainContent) {
          mainContent.scrollTop = 0;
        }
      });
      
      // 在路由变化时更新未读消息数量
      this.fetchUnreadCount();
    }
  },
  computed: {
    ...mapGetters({
      isCollapse: 'isCollapse',
      userName: 'auth/userName',
      userAvatar: 'auth/userAvatar'
    }),
    activeMenu() {
      // 获取当前路径的最后部分作为菜单项标识
      const path = this.$route.path;
      const pathSegments = path.split('/');
      const lastSegment = pathSegments[pathSegments.length - 1];
      
      // 如果最后一段是teacher，表示首页
      if (lastSegment === 'teacher') {
        return this.getRoutePath('');
      }
      
      // 否则使用最后一段作为子路径
      return this.getRoutePath(lastSegment);
    },
    currentYear() {
      return new Date().getFullYear();
    },
    pageTitle() {
      const route = this.$route;
      const path = route.path;
      const basePath = path.split('/').slice(-2).join('/'); // 获取最后两段路径
      
      // 根据路径最后两段判断当前页面
      switch (basePath) {
        case 'teacher':
          return '教师首页';
        case 'teacher/communication':
          return '家长沟通';
        case 'teacher/rating':
          return '学生评分';
        case 'teacher/assignments':
          return '学生作业';
        case 'teacher/attendance':
          return '签到管理';
        case 'teacher/scores':
          return '学生成绩';
        case 'teacher/wrong-questions':
          return '错题记录';
        case 'teacher/documents':
          return '文库查看';
        default:
          return '教师中心';
      }
    },
    sidebarStyle() {
      // 在移动端时，根据折叠状态设置transform
      if (this.isMobile) {
        return {
          transform: this.isCollapse ? 'translateX(-210px)' : 'translateX(0)'
        };
      }
      // 在桌面端时，始终显示侧边栏
      return {
        transform: 'translateX(0)'
      };
    },
    mainContainerStyle() {
      // 在移动端时，侧边栏折叠时外边距为0
      if (this.isMobile) {
        return {
          marginLeft: '0'
        };
      }
      // 在桌面端时，保持外边距
      return {
        marginLeft: '210px'
      };
    },
    schoolCode() {
      return this.$route.params.schoolCode || localStorage.getItem('schoolCode') || '';
    },
    campusCode() {
      return this.$route.params.campusCode || 
        (localStorage.getItem('currentCampus') ? JSON.parse(localStorage.getItem('currentCampus')).code : '') || 
        '';
    }
  },
  methods: {
    ...mapActions(['toggleSidebar']),
    checkScreenSize() {
      const oldIsMobile = this.isMobile;
      this.isMobile = window.innerWidth < 768;
      
      // 如果设备类型变化了，更新侧边栏状态
      if (oldIsMobile !== this.isMobile) {
        // 移动端设置为折叠，桌面端设置为展开
        this.$store.dispatch('toggleSidebar', this.isMobile);
      }
    },
    getRoutePath(path) {
      // 获取学校代码和校区代码
      const schoolCode = this.$route.params.schoolCode || localStorage.getItem('schoolCode') || '';
      const campusCode = this.$route.params.campusCode || localStorage.getItem('campusCode') || '';
      
      // 如果有学校代码和校区代码，使用完整路径
      if (schoolCode && campusCode && schoolCode !== 'undefined' && campusCode !== 'undefined') {
        return path ? `/${schoolCode}/${campusCode}/teacher/${path}` : `/${schoolCode}/${campusCode}/teacher`;
      }
      // 如果只有学校代码，使用带学校代码的路径
      else if (schoolCode && schoolCode !== 'undefined') {
        return path ? `/${schoolCode}/teacher/${path}` : `/${schoolCode}/teacher`;
      }
      // 没有学校代码，使用不带学校代码的路径
      return path ? `/teacher/${path}` : `/teacher`;
    },
    logout() {
      this.$store.dispatch('auth/logout').then(() => {
        const schoolCode = this.schoolCode;
        // 跳转到带有学校代码的登录页
        if (schoolCode) {
          this.$router.push(`/${schoolCode}/login`);
        } else {
          this.$router.push('/login');
        }
      });
    },
    // 打开个人信息对话框
    openProfileDialog() {
      this.profileDialogVisible = true;
      this.fetchUserProfile();
    },
    
    // 获取用户个人信息
    fetchUserProfile() {
      this.profileLoading = true;
      getUserProfile()
        .then(response => {
          if (response && response.code === 200 && response.data) {
            this.profileData = response.data;
            console.log('个人信息获取成功:', response.data);
          } else if (response && typeof response === 'object') {
            // 兼容直接返回数据对象的情况
            this.profileData = response;
            console.log('个人信息获取成功:', response);
          } else {
            this.$message.error('获取个人信息失败：' + (response?.msg || '返回数据格式不正确'));
          }
        })
        .catch(error => {
          console.error('获取个人信息出错:', error);
          this.$message.error('网络错误，请稍后重试');
        })
        .finally(() => {
          this.profileLoading = false;
        });
    },
    
    // 获取性别显示文本
    getGenderText(gender) {
      const genderMap = {
        0: '未知',
        1: '男',
        2: '女'
      };
      return genderMap[gender] || '未知';
    },
    
    // 打开修改密码对话框
    openPasswordDialog() {
      this.passwordDialogVisible = true;
      this.resetPasswordForm();
    },
    
    // 提交修改密码表单
    submitPasswordForm() {
      this.$refs.passwordForm.validate(valid => {
        if (valid) {
          this.changingPassword = true;
          
          changePassword({
            old_password: this.passwordForm.old_password,
            new_password: this.passwordForm.new_password
          })
            .then(response => {
              console.log('修改密码响应:', response);
              // 成功响应可能只是返回null，根据API文档密码修改成功时data是null
              // 所以这里不检查response本身，而是认为到达这个then分支就是成功
              this.$message.success('密码更新成功');
              this.passwordDialogVisible = false;
              
              // 密码修改成功后，可以选择退出登录或者保持登录状态
              // 这里选择保持登录状态，如果需要退出登录，可以取消下面的注释
              // setTimeout(() => {
              //   this.logout();
              // }, 1500);
            })
            .catch(error => {
              console.error('修改密码出错:', error);
              let errorMsg = '网络错误，请稍后重试';
              
              // 处理响应对象中的错误信息
              if (error.response && error.response.data) {
                errorMsg = error.response.data.msg || '密码修改失败';
              } else if (error.data) {
                // 直接从error对象中获取数据
                errorMsg = error.data.msg || '密码修改失败';
              } else if (error.msg) {
                // 直接从error对象中获取msg
                errorMsg = error.msg;
              }
              
              this.$message.error(errorMsg);
            })
            .finally(() => {
              this.changingPassword = false;
            });
        }
      });
    },
    
    // 重置密码表单
    resetPasswordForm() {
      if (this.$refs.passwordForm) {
        this.$refs.passwordForm.resetFields();
      }
      
      this.passwordForm = {
        old_password: '',
        new_password: '',
        confirm_password: ''
      };
    },
    
    // 打开手机号更新对话框
    openPhoneDialog() {
      // 确保已获取用户资料
      if (!this.profileData) {
        this.profileLoading = true;
        getUserProfile()
          .then(response => {
            if (response && response.code === 200 && response.data) {
              this.profileData = response.data;
              console.log('个人信息获取成功:', response.data);
              this.phoneDialogVisible = true;
            } else if (response && typeof response === 'object') {
              // 兼容直接返回数据对象的情况
              this.profileData = response;
              console.log('个人信息获取成功:', response);
              this.phoneDialogVisible = true;
            } else {
              this.$message.error('获取个人信息失败：' + (response?.msg || '返回数据格式不正确'));
            }
          })
          .catch(error => {
            console.error('获取个人信息出错:', error);
            this.$message.error('网络错误，请稍后重试');
          })
          .finally(() => {
            this.profileLoading = false;
          });
      } else {
        this.phoneDialogVisible = true;
      }
    },
    
    // 处理手机号更新成功
    handlePhoneUpdated(newPhone) {
      // 更新用户资料中的手机号
      if (this.profileData) {
        this.profileData.phone = newPhone;
      }
      this.$message.success('手机号已更新为：' + newPhone);
      
      // 手机号更新成功后，延迟1.5秒自动登出
      setTimeout(() => {
        this.$message.info('手机号已更新，请重新登录');
        this.logout();
      }, 1500);
    },
    
    // 获取未读消息数量
    fetchUnreadCount() {
      getUnreadCount()
        .then(response => {
          console.log('获取未读消息响应:', response);
          // 重置未读消息数量
          this.unreadCount = 0;
          
          if (response) {
            // 根据API返回的数据结构设置未读消息数量
            if (response.data && typeof response.data === 'object' && 'count' in response.data) {
              this.unreadCount = response.data.count;
            } else if (typeof response.data === 'number') {
              this.unreadCount = response.data;
            } else if (response.count) {
              this.unreadCount = response.count;
            } else if (typeof response === 'number') {
              this.unreadCount = response;
            }
            console.log('设置未读消息数量:', this.unreadCount);
          }
        })
        .catch(error => {
          console.error('获取未读消息数量失败:', error);
          // 发生错误时也重置未读消息数量
          this.unreadCount = 0;
        });
    },
    
    // 添加方法强制刷新未读消息状态
    refreshUnreadStatus() {
      this.unreadCount = 0;
      this.$nextTick(() => {
        this.fetchUnreadCount();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.teacher-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f7fa;
  
  .top-bar {
    height: 40px;
    background: linear-gradient(to right, #4f46e5, #7e22ce);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    
    .top-bar-content {
      height: 100%;
      display: flex;
      align-items: center;
      padding: 0 20px;
      
      .page-title {
        color: white;
        font-size: 16px;
        font-weight: 500;
      }
    }
  }
  
  .sidebar-mask {
    position: fixed;
    top: 40px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 998;
  }
  
  .sidebar-container {
    width: 210px !important;
    background-color: #fff;
    height: calc(100vh - 40px);
    position: fixed;
    top: 40px;
    left: 0;
    z-index: 999;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;
    overflow-y: auto; /* 允许侧边栏滚动 */
    
    .sidebar-header {
      height: 60px;
      display: flex;
      align-items: center;
      padding: 0 15px;
      border-bottom: 1px solid #f0f0f0;
      
      .logo-wrapper {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #4f46e5;
        color: white;
        border-radius: 4px;
        margin-right: 10px;
        font-weight: bold;
      }
      
      .title-wrapper {
        display: flex;
        flex-direction: column;
        
        .school-title {
          font-size: 16px;
          font-weight: 500;
          color: #303133;
        }
        
        .module-title {
          font-size: 12px;
          color: #909399;
        }
      }
    }
    
    .sidebar-nav {
      .nav-group {
        padding: 15px 0;
        
        .nav-group-title {
          padding: 0 15px 10px;
          font-size: 12px;
          color: #909399;
        }
        
        .sidebar-menu {
          border-right: none;
        }
      }
    }
  }
  
  .main-container {
    transition: margin-left 0.3s;
    margin-top: 40px;
    
    .header {
      background-color: #fff;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0;
      position: fixed;
      top: 40px;
      left: 0;
      right: 0;
      z-index: 997;
      
      .header-left {
        display: flex;
        align-items: center;
        
        .collapse-btn-wrapper {
          position: relative;
          
          .collapse-btn {
            padding: 0 15px;
            font-size: 20px;
            cursor: pointer;
            color: #606266;
          }
          
          .message-badge {
            position: absolute;
            top: 2px;
            right: 3px;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: #F56C6C;
          }
        }
      }
      
      .header-right {
        margin-right: 20px;
        
        .avatar-container {
          display: flex;
          align-items: center;
          cursor: pointer;
          
          .name {
            margin: 0 5px;
            color: #606266;
          }
        }
      }
    }
    
    .main-content {
      padding: 80px 20px 20px;
      background-color: #f5f7fa;
      min-height: calc(100vh - 160px);
    }
    
    .footer {
      background-color: #fff;
      color: #909399;
      text-align: center;
      line-height: 40px;
      border-top: 1px solid #e6e6e6;
    }
  }
  
  // 添加个人信息对话框样式
  .profile-dialog {
    .profile-container {
      padding: 15px;
      max-height: 60vh;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      
      .user-profile {
        .profile-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px 0;
          
          .user-name {
            margin-top: 10px;
            font-size: 18px;
            font-weight: 500;
          }
          
          .user-role {
            font-size: 14px;
            color: #909399;
            margin-top: 5px;
          }
        }
        
        .info-section {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 15px;
          margin-bottom: 20px;
          
          .info-item {
            .info-label {
              font-size: 14px;
              color: #909399;
              margin-bottom: 5px;
            }
            
            .info-value {
              font-size: 14px;
              color: #303133;
            }
          }
        }
        
        .campus-section {
          .campus-item {
            margin-bottom: 15px;
            border: 1px solid #ebeef5;
            border-radius: 4px;
            padding: 10px;
            
            .campus-name {
              font-size: 16px;
              font-weight: 500;
              margin-bottom: 10px;
              display: flex;
              align-items: center;
              
              .el-tag {
                margin-left: 8px;
              }
            }
            
            .campus-details {
              .campus-detail-item {
                display: flex;
                align-items: center;
                margin-bottom: 5px;
                font-size: 14px;
                color: #606266;
                
                i {
                  margin-right: 5px;
                  color: #409EFF;
                }
              }
            }
          }
        }
      }
      
      .empty-profile {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 0;
        
        i {
          font-size: 48px;
          color: #909399;
          margin-bottom: 15px;
        }
        
        .empty-text {
          font-size: 16px;
          color: #909399;
        }
      }
    }
  }
  
  // 修改密码对话框样式
  .password-dialog {
    .el-form {
      width: 80%;
      margin: 0 auto;
      
      @media (min-width: 768px) {
        width: 60%;
      }
      
      .password-action-buttons {
        display: flex;
        justify-content: center;
        gap: 10px;
        
        .el-button {
          padding: 8px 15px;
          min-width: 80px;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .profile-dialog, .password-dialog {
    .el-dialog__body {
      padding: 15px !important;
    }
    
    .info-section {
      grid-template-columns: 1fr !important;
    }
  }
  
  /* 移动端对话框样式 */
  .mobile-dialog {
    width: 95% !important;
    max-width: 500px !important;
    margin: 0 auto !important;
    border-radius: 8px;
    overflow: hidden;
  }
  
  :deep(.el-dialog__header) {
    padding: 15px 20px;
  }
  
  :deep(.el-dialog__body) {
    padding: 15px;
  }
  
  :deep(.el-dialog__footer) {
    padding: 10px 20px;
  }
}

/* 调整家长沟通菜单项样式 */
.communication-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
  left: 3px;
}

.communication-badge {
  margin-left: 4px;
  ::v-deep .el-badge__content {
    height: 16px;
    line-height: 16px;
    padding: 0 4px;
    font-size: 10px;
    border: none;
  }
}
</style>

<style lang="scss">
/* 全局样式，不使用scoped */
.el-badge__content {
  height: 16px !important;
  line-height: 16px !important;
  padding: 0 4px !important;
  font-size: 10px !important;
  border: none !important;
  background-color: #F56C6C !important;
}

/* 调整侧边栏菜单样式 */
.el-menu-item {
  display: flex !important;
  align-items: center !important;
}
</style> 